/**
 * dataProcessor.js
 * Filtrage, agrégation et transformation des données pour visualisation
 */

import { COLLABORATORS } from './config.js';

const DataProcessor = (() => {
  /**
   * Filtrer les entrées par plage de dates
   */
  function filterByDateRange(entries, startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59); // Inclure tout le jour final

    return entries.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate >= start && entryDate <= end;
    });
  }

  /**
   * Grouper les entrées par activité et calculer les heures totales
   * Retour: { activité: totalHeures, ... }
   */
  function groupByActivity(entries) {
    const grouped = {};

    entries.forEach(entry => {
      if (!grouped[entry.activity]) {
        grouped[entry.activity] = 0;
      }
      grouped[entry.activity] += entry.hours;
    });

    return grouped;
  }

  /**
   * Grouper les entrées par client et calculer les heures totales
   * Retour: { client: totalHeures, ... }
   */
  function groupByClient(entries) {
    const grouped = {};

    entries.forEach(entry => {
      if (!grouped[entry.client]) {
        grouped[entry.client] = 0;
      }
      grouped[entry.client] += entry.hours;
    });

    return grouped;
  }

  /**
   * Grouper par collaborateur
   */
  function groupByCollaborator(entries) {
    const grouped = {};

    entries.forEach(entry => {
      if (!grouped[entry.collaborator]) {
        grouped[entry.collaborator] = {
          name: entry.collaboratorName,
          hours: 0,
          entries: []
        };
      }
      grouped[entry.collaborator].hours += entry.hours;
      grouped[entry.collaborator].entries.push(entry);
    });

    return grouped;
  }

  /**
   * Générer un tableau pivot: Client → Activité → Heures
   * Structure:
   * {
   *   client1: {
   *     activities: {
   *       activity1: 10,
   *       activity2: 5
   *     },
   *     total: 15
   *   },
   *   client2: { ... }
   * }
   */
  function generatePivotTable(entries) {
    const pivot = {};

    entries.forEach(entry => {
      // Initialiser le client s'il n'existe pas
      if (!pivot[entry.client]) {
        pivot[entry.client] = {
          activities: {},
          total: 0
        };
      }

      // Initialiser l'activité s'il n'existe pas
      if (!pivot[entry.client].activities[entry.activity]) {
        pivot[entry.client].activities[entry.activity] = 0;
      }

      // Ajouter les heures
      pivot[entry.client].activities[entry.activity] += entry.hours;
      pivot[entry.client].total += entry.hours;
    });

    // Trier par client
    const sorted = {};
    Object.keys(pivot)
      .sort()
      .forEach(client => {
        sorted[client] = pivot[client];
        // Trier aussi les activités
        const sortedActivities = {};
        Object.keys(pivot[client].activities)
          .sort()
          .forEach(activity => {
            sortedActivities[activity] = pivot[client].activities[activity];
          });
        sorted[client].activities = sortedActivities;
      });

    return sorted;
  }

  /**
   * Calculer les statistiques globales
   */
  function getStats(entries) {
    const stats = {
      totalHours: 0,
      totalDays: 0,
      avgHoursPerDay: 0,
      numberOfClients: 0,
      numberOfActivities: 0,
      numberOfCollaborators: 0,
      dateRange: {
        start: null,
        end: null
      }
    };

    if (entries.length === 0) {
      return stats;
    }

    // Heures totales
    stats.totalHours = entries.reduce((sum, entry) => sum + entry.hours, 0);

    // Dates
    const dates = [...new Set(entries.map(e => e.date))].sort();
    stats.dateRange.start = dates[0];
    stats.dateRange.end = dates[dates.length - 1];
    stats.totalDays = dates.length;

    // Moyenne
    stats.avgHoursPerDay = stats.totalHours / (stats.totalDays || 1);

    // Clients uniques
    stats.numberOfClients = new Set(entries.map(e => e.client)).size;

    // Activités uniques
    stats.numberOfActivities = new Set(entries.map(e => e.activity)).size;

    // Collaborateurs uniques
    stats.numberOfCollaborators = new Set(entries.map(e => e.collaborator)).size;

    return stats;
  }

  /**
   * Préparer les données pour Chart.js (activités)
   * Retour: { labels: [], data: [] }
   */
  function prepareActivityChartData(entries) {
    const grouped = groupByActivity(entries);

    // Trier par heures (décroissant)
    const sorted = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1]);

    return {
      labels: sorted.map(([activity, _]) => activity),
      data: sorted.map(([_, hours]) => parseFloat(hours.toFixed(1)))
    };
  }

  /**
   * Préparer les données pour Chart.js (clients)
   * Retour: { labels: [], data: [] }
   */
  function prepareClientChartData(entries) {
    const grouped = groupByClient(entries);

    // Trier par heures (décroissant)
    const sorted = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1]);

    return {
      labels: sorted.map(([client, _]) => client),
      data: sorted.map(([_, hours]) => parseFloat(hours.toFixed(1)))
    };
  }

  /**
   * Préparer données timeline (évolution par mois)
   */
  function prepareTimelineData(entries) {
    const monthlyData = {};

    entries.forEach(entry => {
      const date = new Date(entry.date);
      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData[month]) {
        monthlyData[month] = 0;
      }
      monthlyData[month] += entry.hours;
    });

    const sorted = Object.entries(monthlyData).sort();

    return {
      labels: sorted.map(([month, _]) => {
        const [year, m] = month.split('-');
        return new Date(`${year}-${m}-01`).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      }),
      data: sorted.map(([_, hours]) => parseFloat(hours.toFixed(1)))
    };
  }

  /**
   * Générer récapitulatif par collaborateur
   */
  function prepareCollaboratorSummary(entries) {
    const grouped = groupByCollaborator(entries);

    const summary = Object.entries(grouped)
      .map(([id, data]) => {
        const collab = COLLABORATORS[id];
        const clients = new Set(data.entries.map(e => e.client)).size;
        return {
          name: collab ? collab.name : data.name,
          structure: collab ? collab.structure : 'Unknown',
          hours: parseFloat(data.hours.toFixed(1)),
          clients
        };
      })
      .sort((a, b) => b.hours - a.hours);

    return summary;
  }

  return {
    filterByDateRange,
    groupByActivity,
    groupByClient,
    groupByCollaborator,
    generatePivotTable,
    getStats,
    prepareActivityChartData,
    prepareClientChartData,
    prepareTimelineData,
    prepareCollaboratorSummary
  };
})();

export default DataProcessor;
